import { createI18n } from 'vue-i18n';
import en from './locales/en.json';
import tr from './locales/tr.json';
import sq from './locales/sq.json';
import it from './locales/it.json';

export const SUPPORTED_LOCALES = ['en', 'tr', 'sq', 'it'] as const;
export type Locale = (typeof SUPPORTED_LOCALES)[number];

export const isLocale = (value: string): value is Locale => {
  return (SUPPORTED_LOCALES as readonly string[]).includes(value);
};

// Detect locale: URL prefix (SEO-friendly) → localStorage → browser → default
const getInitialLocale = (): Locale => {
  // 1. URL prefix takes priority so Google can crawl per-language URLs
  const path = window.location.pathname;
  if (/^\/(sq)(\/|$)/.test(path)) return 'sq';
  if (/^\/(tr)(\/|$)/.test(path)) return 'tr';
  if (/^\/(it)(\/|$)/.test(path)) return 'it';

  // 2. Stored user preference
  const saved = localStorage.getItem('locale');
  if (saved && isLocale(saved)) return saved;

  // 3. Browser language
  const browserLang = navigator.language.split('-')[0];
  if (isLocale(browserLang)) return browserLang;

  return 'en';
};

const i18n = createI18n({
  legacy: false,
  locale: getInitialLocale(),
  fallbackLocale: 'en',
  messages: {
    en,
    tr,
    sq,
    it,
  },
});

// Function to change locale and persist to localStorage
export const setLocale = (locale: Locale) => {
  i18n.global.locale.value = locale;
  localStorage.setItem('locale', locale);
  document.documentElement.lang = locale;
};

export default i18n;
